<template><div><code-well-header class="d-d-block">
  <ul>
    <dt-list-item navigation-type="tab">
      <template #left>
        <dt-icon name="check" />
      </template>
      <span>Default List Item</span>
      <template #subtitle>
        Description
      </template>
      <template #bottom>
        <dt-badge text="Label" />
      </template>
      <template #right>
        <dt-icon name="external-link" />
      </template>
    </dt-list-item>
  </ul>
</code-well-header>
<h2 id="base-style" tabindex="-1" class="d-docsite--header-2">Base Style <a class="header-anchor d-link d-docsite--link d-link" href="#base-style" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A list item provides accessibility controls and common functionality. The component uses child components that provide styling and slots for different types of list items. If you want to create a custom list item you can pass a type &quot;custom&quot;, which will let you define the structure of the content.</p>
<p class="d-docsite--paragraph">The default list item has 5 slots that can be used for the most common use cases, <strong>left</strong>, <strong>right</strong>, <strong>default</strong>, <strong>subtitle</strong> and <strong>bottom</strong> slot. All of the slots are optional.</p>
<p class="d-docsite--paragraph">The <strong>left</strong> slot can contain content, such as an avatar, that will be positioned to the left of the main content.</p>
<p class="d-docsite--paragraph">The <strong>right</strong> slot works the same way, but its contents are placed to the right of the main slot.</p>
<p class="d-docsite--paragraph">The <strong>default</strong> slot contains the main content of the list item.</p>
<p class="d-docsite--paragraph">The <strong>subtitle</strong> slot can be used to display content below the default slot. The slot has smaller text size and lighter color than default slot.</p>
<p class="d-docsite--paragraph">The <strong>bottom</strong> slot can be used to display content below the subtitle slot.</p>
<code-well-header class="d-d-block">
  <ul>
    <dt-list-item navigation-type="tab">
      <template #left>
        <dt-icon name="check" />
      </template>
      <span>Default List Item</span>
      <template #subtitle>
        Description
      </template>
      <template #bottom>
        <dt-badge text="Label" />
      </template>
      <template #right>
        <dt-icon name="external-link" />
      </template>
    </dt-list-item>
  </ul>
</code-well-header>
<code-example-tabs
htmlCode='
<ul>
  <li id="dt4" class="d-list-item d-list-item--focusable" tabindex="0" role="listitem">
    <div class="d-item-layout">
      <section class="d-item-layout--left">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-500" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </section>
      <section class="d-item-layout--content">
        <div class="d-item-layout--subtitle">
          {subtitle}
        </div>
        <div class="d-item-layout--bottom">
          {bottom}
        </div>
      </section>
      <section class="d-item-layout--right">
        <span class="d-icon__wrapper">
          <div aria-busy="true" role="status" aria-label="" class="d-icon d-icon--size-500" style="display: none;">
            <div
              class="d-skeleton-placeholder d-bar-circle d-skeleton-placeholder--animate"
              style="animation-delay: 0ms; animation-duration: 1000ms; min-width: 100%; max-width: 100%; min-height: 100%; max-height: 100%;"
            ></div>
          </div>
          <svg>...</svg>
        </span>
      </section>
    </div>
  </li>
</ul>
'
vueCode='
<ul>
  <dt-list-item navigation-type="tab">
    <template #left>
      <dt-icon name="check" />
    </template>
    <span>Default List Item</span>
    <template #subtitle>
      Description
    </template>
    <template #bottom>
      <dt-badge text="Label" />
    </template>
    <template #right>
      <dt-icon name="external-link" />
    </template>
  </dt-list-item>
</ul>
'
/>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="custom-list-item" tabindex="-1" class="d-docsite--header-3">Custom List Item <a class="header-anchor d-link d-docsite--link d-link" href="#custom-list-item" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When <code v-pre>type</code> is set to &quot;custom&quot; the list item will not render any styles or slots. This type can be used when the list item has to support content that does not work with the default structure.</p>
<code-well-header class="d-d-block">
  <ul ref="customListItem">
    <dt-list-item
      navigation-type="tab"
      type="custom"
    >
      <dt-stack direction="row" align="center" justify="between" class="d-py8 d-px12">
        <div>
          <time
            class="d-fs-100 d-pr12"
            datetime="10:00"
          >
            10:00
          </time>
          <span class="d-fs-200">
            Custom List Item Example
          </span>
        </div>
        <dt-stack direction="row" class="d-p6 d-tn8 d-r12 d-bgc-white d-bar4 d-bs-md">
          <dt-button
            class="d-p4 d-py8"
            importance="clear"
            title="share"
          >
            <template #icon>
              <dt-icon
                name="share-2"
                size="200"
              />
            </template>
          </dt-button>
          <dt-button
            class="d-p4 d-py8 d-ml4"
            importance="clear"
            title="star"
          >
            <template #icon>
              <dt-icon
                name="star"
                size="200"
              />
            </template>
          </dt-button>
          <dt-button
            class="d-p4 d-py8 d-ml4"
            importance="clear"
            title="more"
          >
            <template #icon>
              <dt-icon
                name="more-vertical"
                size="200"
              />
            </template>
          </dt-button>
        </dt-stack>
      </dt-stack>
    </dt-list-item>
  </ul>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.customListItem'
vueCode='
<ul>
  <dt-list-item
    navigation-type="tab"
    type="custom"
  >
    <dt-stack direction="row" align="center" justify="between" class="d-py8 d-px12">
      <div>
        <time
          class="d-fs-100 d-pr12"
          datetime="10:00"
        >
          10:00
        </time>
        <span class="d-fs-200">
          Custom List Item Example
        </span>
      </div>
      <dt-stack direction="row" class="d-p6 d-tn8 d-r12 d-bgc-white d-bar4 d-bs-md">
        <dt-button
          class="d-p4 d-py8"
          importance="clear"
          title="share"
        >
          <template #icon>
            <dt-icon
              name="share-2"
              size="200"
            />
          </template>
        </dt-button>
        <dt-button
          class="d-p4 d-py8 d-ml4"
          importance="clear"
          title="star"
        >
          <template #icon>
            <dt-icon
              name="star"
              size="200"
            />
          </template>
        </dt-button>
        <dt-button
          class="d-p4 d-py8 d-ml4"
          importance="clear"
          title="more"
        >
          <template #icon>
            <dt-icon
              name="more-vertical"
              size="200"
            />
          </template>
        </dt-button>
      </dt-stack>
      </dt-stack>
  </dt-list-item>
</ul>
'
/>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="listitem" /></div></template>


